import { computed, ComputedRef, reactive } from "vue"
import axios from "axios"
import { isNullish } from "@tinyurldev/tu-ui-vue"
import type { ApiToken, ApiTokenPayload } from "../Data/Models/ApiToken"
import useNotify from "@Composables/useNotify"
import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter"
import { ParseServerError, ProcessServerErrors } from "@Modules/shared/Api/ServerErrors"

export type ApiTokensApi = {
    tokens: ComputedRef<Record<string, ApiToken[]>>
    TeamTokens: ComputedRef<(teamSlug: string | null) => ApiToken[]>
    loading: ComputedRef<boolean>
    errors: ComputedRef<Record<string, string>>
    fetchTeamTokens: (args: { orgSlug: string; teamSlug: string }) => Promise<void>
    createToken: (args: {
        orgSlug: string
        teamSlug: string
        payload: ApiTokenPayload
    }) => Promise<ApiToken | null>
    updateToken: (args: {
        orgSlug: string
        teamSlug: string
        tokenId: number
        payload: ApiTokenPayload
    }) => Promise<ApiToken | null>
    deleteToken: (args: {
        orgSlug: string
        teamSlug: string
        tokenId: number
    }) => Promise<boolean>
    setRouter: (router: HttpRouter<"get" | "post" | "delete">) => ApiTokensApi
    clearTokens: () => void
    clearErrors: () => void
}

/**
 * ApiTokensApi is used to manage team API tokens
 */
export default function ApiTokensApi(): ApiTokensApi {
    const state = reactive<{
        tokens: Record<string, ApiToken[]>
        loading: boolean
        errors: Record<string, string>
    }>({
        tokens: {},
        loading: false,
        errors: {},
    })

    let router: HttpRouter<"get" | "post" | "delete"> = axios
    const { notifySuccess, notifyError } = useNotify()

    function clearErrors() {
        Object.keys(state.errors).forEach((key) => delete state.errors[key])
    }

    const that: ApiTokensApi = {
        tokens: computed(() => state.tokens),

        /**
         * Returns the API tokens belonging to the provided team
         * @param String teamSlug the slug of the team we want the tokens for
         */
        TeamTokens: computed(() => (teamSlug: string | null): ApiToken[] => {
            if (isNullish(teamSlug)) {
                return []
            }

            return state.tokens[teamSlug] ?? []
        }),
        loading: computed(() => state.loading),
        errors: computed(() => state.errors),
        clearTokens: () => {
            state.tokens = {}
        },
        clearErrors,

        /**
         * Fetch all API tokens for a team
         */
        fetchTeamTokens: async function ({
            orgSlug,
            teamSlug,
        }: {
            orgSlug: string
            teamSlug: string
        }) {
            clearErrors()
            state.loading = true
            const url = `/app/api/organization/${orgSlug}/team/${teamSlug}/api_tokens`

            try {
                const response = await router.get<{ data?: ApiToken[] }>(url)

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not fetch tokens for team ${teamSlug}`)
                }

                state.tokens = {
                    ...state.tokens,
                    [teamSlug]: response.data?.data || [],
                }
            } catch (e) {
                notifyError({ text: ParseServerError(e).serverMessage || "Failed to fetch tokens." })
                throw e
            } finally {
                state.loading = false
            }
        },

        /**
         * Create a new API token for a team
         */
        createToken: async function ({
            orgSlug,
            teamSlug,
            payload,
        }: {
            orgSlug: string
            teamSlug: string
            payload: ApiTokenPayload
        }): Promise<ApiToken | null> {
            clearErrors()
            state.loading = true
            const url = `/app/api/organization/${orgSlug}/team/${teamSlug}/api_token`

            try {
                const response = await router.post<{ data: ApiToken }>(url, payload)

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not create token for team ${teamSlug}`)
                }

                const newToken: ApiToken = response.data.data

                // Add token to local state
                const currentTokens = state.tokens[teamSlug] || []
                state.tokens = {
                    ...state.tokens,
                    [teamSlug]: [newToken, ...currentTokens],
                }

                notifySuccess({ text: "The token has been added." })
                return newToken
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({ text: serverMessage || "Failed to create token." })
                }
                return null
            } finally {
              state.loading = false;
            }
        },

        /**
         * Update an existing API token
         */
        updateToken: async function ({
            orgSlug,
            teamSlug,
            tokenId,
            payload,
        }: {
            orgSlug: string
            teamSlug: string
            tokenId: number
            payload: ApiTokenPayload
        }): Promise<ApiToken | null> {
            clearErrors()
            state.loading = true
            const url = `/app/api/organization/${orgSlug}/team/${teamSlug}/api_token/${tokenId}`

            try {
                // Using POST with _method: PUT for Laravel compatibility
                const response = await router.post<{ data: ApiToken }>(url, { ...payload, _method: "PUT" })

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not update token ${tokenId}`)
                }

                const updatedToken: ApiToken = response.data.data

                // Update token in local state
                const currentTokens = state.tokens[teamSlug] || []
                state.tokens = {
                    ...state.tokens,
                    [teamSlug]: currentTokens.map((t) => (t.id === tokenId ? updatedToken : t)),
                }

                notifySuccess({ text: "The token has been updated." })
                return updatedToken
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({ text: serverMessage || "Failed to update token." })
                }
                return null
            } finally {
              state.loading = false;
            }
        },

        /**
         * Delete an API token
         */
        deleteToken: async function ({
            orgSlug,
            teamSlug,
            tokenId,
        }: {
            orgSlug: string
            teamSlug: string
            tokenId: number
        }): Promise<boolean> {
            clearErrors()
            state.loading = true
            const url = `/app/api/organization/${orgSlug}/team/${teamSlug}/api_token/${tokenId}`

            try {
                const response = await router.delete(url)

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not delete token ${tokenId}`)
                }

                // Remove token from local state
                const currentTokens = state.tokens[teamSlug] || []
                state.tokens = {
                    ...state.tokens,
                    [teamSlug]: currentTokens.filter((t) => t.id !== tokenId),
                }

                notifySuccess({ text: "The token has been deleted." })
                return true
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({ text: serverMessage || "Failed to delete token." })
                }
                return false
            } finally {
              state.loading = false;
            }
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter: HttpRouter<"get" | "post" | "delete">) {
            router = newRouter
            return that
        },
    }

    return that
}
